import { useState, useEffect, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { LuSettings } from 'react-icons/lu';
import { Route } from '@/routes/agent_spa/agent/alliance/index';
import Tabs from '@/components/Tabs';
import ApplyList from '../components/ApplyList';
import ApplyListHeader from '../components/ApplyListHeader';
import ApplyListSkeleton from '../components/ApplyListSkeleton';
import EmptyApplicationList from '../components/EmptyApplicationList';
import ApplicationMemoModal from '../components/ApplicationMemoModal';
import StatusChangeConfirmModal from '../components/StatusChangeConfirmModal';
import AllianceSettings from '../components/AllianceSettings';
import AgentManagement from '../components/AgentManagement';
import ApplicationFilterBar from '../components/ApplicationFilterBar';
import useActiveUsers from '@/features/agent/active_users/hooks/useActiveUsers';
import useFetchAgentAllianceApplications from '../api/useFetchAllianceApplications';
import type { AllianceSortOrder } from '../api/useFetchAllianceApplications';
import useUpdateAgentAllianceApplicationStatus from '../api/useUpdateAgentAllianceApplicationStatus';
import useUpdateAllianceCompanyAssignees from '../api/useUpdateAllianceCompanyAssignees';
import useUpdateAgentAllianceApplicationMemo from '../api/useUpdateAgentAllianceApplicationMemo';
import { showSuccessToast, showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import { getErrorMessage } from '../utils/getErrorMessage';
import type { AgentApiAllianceApplicationsItem } from '@/types/serializers';

type TabValue = 'applications' | 'settings' | 'agents';

export default function AllianceTopTemplate() {
  const { tab, q, sort, status, agentId } = Route.useSearch();
  const activeTab = tab ?? 'applications';
  const navigate = useNavigate({ from: '/agent/alliance' });

  const [localKeyword, setLocalKeyword] = useState(q ?? '');
  const [memoTarget, setMemoTarget] = useState<AgentApiAllianceApplicationsItem | null>(null);
  const [statusTarget, setStatusTarget] = useState<{
    application: AgentApiAllianceApplicationsItem;
    status: string;
  } | null>(null);

  // 入力とURLの往復で値を上書きし合わないよう、最後に同期した値を覚えておく
  const syncedKeywordRef = useRef(q ?? '');

  // 入力 → URL（デバウンス）
  useEffect(() => {
    if (localKeyword === syncedKeywordRef.current) return;
    const timer = setTimeout(() => {
      syncedKeywordRef.current = localKeyword;
      void navigate({
        to: '/agent/alliance',
        search: (prev) => ({ ...prev, q: localKeyword || undefined }),
        replace: true,
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [localKeyword]);

  // URL → 入力（ブラウザバック等、自分の入力以外で q が変わったときだけ反映する）
  useEffect(() => {
    const nextKeyword = q ?? '';
    if (nextKeyword === syncedKeywordRef.current) return;
    syncedKeywordRef.current = nextKeyword;
    setLocalKeyword(nextKeyword);
  }, [q]);

  const setActiveTab = (newTab: TabValue) => {
    void navigate({
      to: '/agent/alliance',
      search: { tab: newTab === 'applications' ? undefined : newTab },
      replace: true,
    });
  };

  const openApplicationDetail = (uid: string) => {
    void navigate({
      to: '/agent/alliance/applications/$applicationId',
      params: { applicationId: uid },
    });
  };

  const handleStatusFilterChange = (value: string) => {
    void navigate({
      to: '/agent/alliance',
      search: (prev) => ({
        ...prev,
        status: value === 'all' ? undefined : value,
      }),
      replace: true,
    });
  };

  const handleSortChange = (value: AllianceSortOrder) => {
    void navigate({
      to: '/agent/alliance',
      search: (prev) => ({ ...prev, sort: value === 'updated_at_desc' ? undefined : value }),
      replace: true,
    });
  };

  const handleAgentIdChange = (value: string) => {
    void navigate({
      to: '/agent/alliance',
      search: (prev) => ({
        ...prev,
        agentId: value === 'all' ? undefined : value,
      }),
      replace: true,
    });
  };

  const handleClearFilters = () => {
    setLocalKeyword('');
    void navigate({
      to: '/agent/alliance',
      // タブ（status）は検索条件ではないので維持する
      search: (prev) => ({ status: prev.status }),
      replace: true,
    });
  };

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: applicationsData, isLoading: isApplicationsLoading } =
    useFetchAgentAllianceApplications({
      q: q,
      sort: sort,
      status: status,
      agentId: agentId,
    });

  const { data: activeUsersData } = useActiveUsers();
  const activeUsers = activeUsersData?.activeUsers ?? [];

  const updateStatus = useUpdateAgentAllianceApplicationStatus();
  const updateCompanyAssignees = useUpdateAllianceCompanyAssignees();
  const updateMemo = useUpdateAgentAllianceApplicationMemo();

  const applications = applicationsData?.records ?? [];

  const handleStatusChange = (id: string, newStatus: string) => {
    setErrorMessage(null);
    updateStatus.mutate(
      { id, status: newStatus },
      {
        onSuccess: () => {
          setStatusTarget(null);
          showSuccessToast('ステータスを更新しました');
        },
        onError: (error) => {
          setErrorMessage(getErrorMessage(error, 'ステータスの更新に失敗しました'));
        },
      }
    );
  };

  const handleAssigneesChange = (companyId: number, userIds: number[]) => {
    updateCompanyAssignees.mutate(
      { companyId, organizationUserIds: userIds },
      {
        onError: (error) => {
          showErrorToast(getErrorMessage(error, '担当者の更新に失敗しました'));
        },
      }
    );
  };

  const handleMemoSave = (memo: string) => {
    if (!memoTarget) return;
    updateMemo.mutate(
      { id: memoTarget.uid, internalMemo: memo },
      {
        onSuccess: () => {
          setMemoTarget(null);
          showSuccessToast('社内メモを保存しました');
        },
        onError: (error) => {
          showErrorToast(getErrorMessage(error, '社内メモの更新に失敗しました'));
        },
      }
    );
  };

  const isSettingsView = activeTab === 'settings' || activeTab === 'agents';

  const settingsTabs = [
    {
      label: '設定',
      current: activeTab === 'settings',
      onClick: () => setActiveTab('settings'),
    },
    {
      label: 'エージェント管理',
      current: activeTab === 'agents',
      onClick: () => setActiveTab('agents'),
    },
  ];

  const currentStatus = status ?? 'all';
  const formatCount = (count: number | undefined) => (count === undefined ? '--' : count);
  const statusTabs = [
    { value: 'all', label: '全て', count: applicationsData?.all_count },
    { value: 'new', label: '新規応募', count: applicationsData?.new_count },
    { value: 'screening', label: '選考中', count: applicationsData?.screening_count },
    { value: 'offered', label: '内定', count: applicationsData?.offered_count },
    { value: 'completed', label: '終了', count: applicationsData?.completed_count },
  ].map((item) => ({
    label: `${item.label} (${formatCount(item.count)})`,
    current: currentStatus === item.value,
    onClick: () => handleStatusFilterChange(item.value),
  }));

  return (
    <div className="bg-background flex flex-col gap-sm overflow-x-hidden px-lg pt-3 pb-lg">
      {errorMessage && (
        <div className="rounded border border-solid border-danger bg-red-50 p-sm">
          <p className="text-body-sm text-danger">{errorMessage}</p>
        </div>
      )}

      {isSettingsView ? (
        <>
          <Tabs items={settingsTabs} />
          {activeTab === 'settings' && <AllianceSettings />}
          {activeTab === 'agents' && <AgentManagement />}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <Tabs items={statusTabs} className="w-[915px]" />
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className="flex size-[42px] items-center justify-center rounded border border-solid border-border bg-white transition-colors hover:bg-gray-50"
              title="設定"
            >
              <LuSettings className="size-5 text-main" />
            </button>
          </div>
          <ApplicationFilterBar
            keyword={localKeyword}
            sort={sort ?? 'updated_at_desc'}
            agentId={agentId ?? 'all'}
            onKeywordChange={setLocalKeyword}
            onSortChange={handleSortChange}
            onAgentIdChange={handleAgentIdChange}
            onClear={handleClearFilters}
          />
          <div className="flex flex-col gap-xs">
            <ApplyListHeader />
            {isApplicationsLoading ? (
              <ApplyListSkeleton />
            ) : applications.length === 0 ? (
              <EmptyApplicationList />
            ) : (
              <div className="flex flex-col gap-xs">
                {applications.map((application) => (
                  <ApplyList
                    key={application.id}
                    application={application}
                    activeUsers={activeUsers}
                    onStatusChange={(newStatus) =>
                      setStatusTarget({ application, status: newStatus })
                    }
                    onAssigneesChange={handleAssigneesChange}
                    onMemoClick={() => setMemoTarget(application)}
                    onDetailClick={() => openApplicationDetail(application.uid)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      <StatusChangeConfirmModal
        open={statusTarget !== null}
        onClose={() => setStatusTarget(null)}
        onConfirm={() => {
          if (!statusTarget) return;
          handleStatusChange(statusTarget.application.uid, statusTarget.status);
        }}
        candidateName={statusTarget?.application.candidate_name ?? ''}
        agentName={statusTarget?.application.agent_name ?? ''}
        status={statusTarget?.status ?? ''}
        isSubmitting={updateStatus.isPending}
      />

      <ApplicationMemoModal
        open={memoTarget !== null}
        onClose={() => setMemoTarget(null)}
        onSave={handleMemoSave}
        memo={memoTarget?.internal_memo ?? null}
        isSubmitting={updateMemo.isPending}
      />
    </div>
  );
}
